import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import axios from "axios";
import { ArrowRight, BookOpen, Radio, Music, Rss, Mail, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUTHOR_IMG = "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/95h4b3ir_Don%20Inbody.jpg";
const VOTING_COVER = "https://static.prod-images.emergentagent.com/jobs/b16508fb-0ce2-444b-ad39-aeb00d336b86/images/ebc6270a8312cb2c289de19c674bd90b1d309c4c8b2c316e0c236e444adaf2b4.jpeg";
const FLIGHT_COVER = "https://static.prod-images.emergentagent.com/jobs/b16508fb-0ce2-444b-ad39-aeb00d336b86/images/bcff0d48331eb785bf0fc83e34b73c837475a9d0e6d07457db6d70d9c430b9ef.jpeg";

export default function Home() {
  const [email, setEmail] = useState("");
  const [subMsg, setSubMsg] = useState("");
  const [recentPosts, setRecentPosts] = useState([]);
  const [latestItems, setLatestItems] = useState([]);
  const [latestLoading, setLatestLoading] = useState(true);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Donald S. Inbody",
      "alternateName": ["Don Inbody", "Donald Stephen Inbody", "Donald Inbody"],
      "honorificPrefix": "Captain",
      "jobTitle": "Author, Political Scientist, Naval Historian",
      "description": "Retired U.S. Navy Captain, political scientist, and author of The Soldier Vote (Palgrave Macmillan) and the forthcoming naval fiction series The Morse Chronicles. Licensed amateur radio operator ADØK. Musician with BuffaloGrass.",
      "url": "https://inbody.net",
      "image": "https://customer-assets.emergentagent.com/job_morse-chronicles/artifacts/95h4b3ir_Don%20Inbody.jpg",
      "sameAs": [
        "https://scholar.google.com/citations?user=WypJ78AAAAAJ&hl=en",
        "https://www.qrz.com/db/AD0K"
      ],
      "knowsAbout": ["Military Voting Rights", "Civil-Military Relations", "Naval History", "Amateur Radio", "Political Science", "Historical Fiction"],
      "alumniOf": { "@type": "CollegeOrUniversity", "name": "University of Texas at Austin" }
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then(r => setRecentPosts(r.data.slice(0, 3))).catch(() => {});
    axios.get(`${API}/latest`)
      .then(r => setLatestItems(r.data))
      .catch(() => {})
      .finally(() => setLatestLoading(false));
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/mailing-list`, { email });
      setSubMsg(res.data.message);
      setEmail("");
    } catch {
      setSubMsg("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        canonical="https://inbody.net"
        keywords="author, naval historian, political scientist, Americana music, BuffaloGrass, ADØK amateur radio, The Soldier Vote, Wake of the Falcon, Captain Hank Morse, USS Wichita, military voting, civil-military relations, inbody.net"
      />
      <Navbar />

      {/* ── Cinematic Hero ── */}
      <section data-testid="hero-section" className="min-h-[100svh] relative flex items-center bg-black overflow-hidden text-[#F5F0E8] pt-24 lg:pt-0">

        {/* Subtle vignette top & bottom */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/30 pointer-events-none z-10" />

        <div className="relative z-20 w-full max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-0">

            {/* ── Left: Identity & CTAs ── */}
            <div className="lg:col-span-5 flex flex-col py-24 pr-0 lg:pr-8">
              <h1 
                className="font-serif text-6xl sm:text-8xl lg:text-9xl font-black text-[#F5F0E8] leading-[0.85] tracking-tighter mb-6"
                aria-label="Donald S. Inbody — Author, Naval Historian and Political Scientist"
              >
                Don<br />Inbody
              </h1>
              <div className="w-24 h-1 bg-[#C5A059] mb-8" style={{ boxShadow: "0 0 15px rgba(197,160,89,0.4)" }} />
              <p className="text-[#C5A059] text-lg sm:text-xl lg:text-2xl font-light mb-10 tracking-[0.2em] uppercase">
                Author &middot; Historian &middot; Musician
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/books"
                  data-testid="hero-cta-books"
                  className="inline-flex items-center justify-center gap-3 bg-[#C5A059] text-[#0A1628] px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#F5F0E8] transition-colors duration-300"
                >
                  View Publications <ArrowRight size={16} />
                </Link>
                <Link
                  to="/blog"
                  data-testid="hero-cta-blog"
                  className="inline-flex items-center justify-center gap-3 border border-[#F5F0E8]/30 text-[#F5F0E8] px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-white/10 hover:border-[#F5F0E8] transition-all duration-300"
                >
                  The Captain's Log
                </Link>
              </div>
            </div>

            {/* ── Right: Author Photo ── */}
            <div className="hidden lg:flex lg:col-span-7 justify-end items-center h-screen relative">
              <img
                src={AUTHOR_IMG}
                alt="Donald S. Inbody — Retired U.S. Navy Captain, political scientist and author"
                data-testid="author-photo-hero"
                className="h-[75vh] w-auto object-cover object-top"
                style={{
                  filter: "brightness(0.92) contrast(1.02)",
                  maskImage: "linear-gradient(to right, transparent 0%, black 15%, black 100%), linear-gradient(to bottom, transparent 0%, black 5%, black 88%, transparent 100%)",
                  maskComposite: "intersect",
                  WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 15%, black 100%), linear-gradient(to bottom, transparent 0%, black 5%, black 88%, transparent 100%)",
                  WebkitMaskComposite: "source-in",
                }}
              />
            </div>

          </div>
        </div>
      </section>

      {/* ── Bio ── */}
      <section data-testid="bio-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <p className="overline mb-3">About the Author</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
                Sailor-Scholar
              </h2>
              <div className="space-y-4 text-[#2D3142] leading-relaxed text-base">
                <p>
                  Don Inbody brings the precision of academic scholarship and the discipline of naval service to everything he creates — from rigorous political science and historical research to page-turning fiction.
                </p>
                <p>
                  His academic work, anchored by <em>The Soldier Vote</em>, explores the relationship between military service and democratic participation. <em>Voting From Afar</em>, a more fully developed history of absentee voting in America, is now under contract with Palgrave and will be released in summer 2028. A second history, <em>Flight Over Water</em>, the story of FDR's secret journey from Washington, DC, to Casablanca in January 1943, is also under contract. His upcoming novel, <em>Wake of the Falcon</em>, still out on query, brings the Navy, History, and communications to light.
                </p>
                <p>
                  When not writing, Don can be found behind the microphone at his amateur radio station ADØK.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-8">
                {[
                  { label: "Published Works", value: "1+" },
                  { label: "Blog Posts", value: "Growing" },
                  { label: "Callsign", value: "ADØK" },
                ].map(({ label, value }) => (
                  <div key={label} data-testid={`bio-stat-${label.toLowerCase().replace(/ /g, '-')}`}>
                    <div className="font-serif text-2xl font-bold text-[#0A1628]">{value}</div>
                    <div className="text-xs text-[#2D3142]/60 uppercase tracking-widest">{label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative w-64 h-80 lg:w-72 lg:h-96">
                <img
                  src={AUTHOR_IMG}
                  alt="Donald S. Inbody — Retired U.S. Navy Captain, political scientist and author"
                  data-testid="author-photo"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute -bottom-3 -right-3 w-full h-full border border-[#C5A059] -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Now Under Contract ── */}
      <section data-testid="under-contract-section" className="py-20 bg-[#0A1628]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12">
            <div className="h-px flex-1 bg-[#C5A059]/20" />
            <p className="overline text-[#C5A059]/80">Now Under Contract</p>
            <div className="h-px flex-1 bg-[#C5A059]/20" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 max-w-3xl mx-auto">
            {[
              { img: VOTING_COVER, series: "Palgrave Macmillan", title: "Voting From Afar", titleBreak: ["Voting From", "Afar"], sub: "The Origins and Evolution of Absentee Voting in America", when: "Summer 2028" },
              { img: FLIGHT_COVER, series: "American Military Experience", title: "Flight Over Water", titleBreak: ["Flight Over", "Water"], sub: "FDR's Secret Flight to Casablanca", when: "Fall 2028" },
            ].map((b) => (
              <div key={b.title} data-testid={`under-contract-${b.title.toLowerCase().replace(/ /g, '-')}`} className="flex gap-5 items-center">
                <div className="relative w-28 aspect-[2/3] shadow-2xl overflow-hidden flex-shrink-0" style={{ background: "#06111f" }}>
                  <img src={b.img} alt={`${b.title} — cover mockup`} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#000]/85 via-[#000]/20 to-[#000]/40" />
                  <div className="absolute top-0 left-0 right-0 px-2.5 pt-2.5">
                    <div className="border-t border-[#C5A059]/60 pt-1">
                      <p className="text-[#C5A059]/85 text-[6px] uppercase tracking-[0.2em] font-mono text-center leading-tight">{b.series}</p>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 px-2 pb-3 text-center">
                    <h3 className="font-serif text-[13px] font-black text-[#F5F0E8] leading-tight drop-shadow-lg">{b.titleBreak[0]}<br />{b.titleBreak[1]}</h3>
                    <div className="h-px w-5 bg-[#C5A059]/70 mx-auto my-1" />
                    <p className="text-[#F5F0E8]/70 text-[6px] font-mono uppercase tracking-widest">Donald S. Inbody</p>
                  </div>
                </div>
                <div>
                  <span className="inline-flex items-center gap-1.5 bg-[#C5A059] text-[#0A1628] text-[9px] font-mono font-bold uppercase tracking-widest px-2.5 py-1 mb-3">
                    <BookOpen size={10} /> Under Contract
                  </span>
                  <h3 className="font-serif text-xl font-bold text-[#F5F0E8] leading-tight">{b.title}</h3>
                  <p className="text-[#F5F0E8]/50 text-xs italic mt-1 mb-2">{b.sub}</p>
                  <p className="text-[#C5A059]/80 text-[11px] font-mono uppercase tracking-widest">Expected {b.when}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/books" data-testid="under-contract-cta" className="inline-flex items-center gap-2 text-[#C5A059] text-sm font-semibold hover:gap-4 transition-all duration-300">
              See all forthcoming books <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Feature Cards Bento Grid ── */}
      <section data-testid="features-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="overline mb-3 text-center">Explore My Work</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0A1628] text-center mb-12">
            Four Worlds, One Author
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
            {/* Books - large */}
            <Link
              to="/books"
              data-testid="feature-card-books"
              className="bento-card lg:col-span-5 bg-[#0A1628] p-8 group flex flex-col justify-between min-h-64"
            >
              <div>
                <div className="w-10 h-10 bg-[#C5A059] flex items-center justify-center mb-6">
                  <BookOpen size={20} className="text-[#0A1628]" />
                </div>
                <p className="overline text-[#C5A059] mb-2">Academic & Fiction</p>
                <h3 className="font-serif text-2xl font-bold text-[#F5F0E8] mb-3">Books & Research</h3>
                <p className="text-[#F5F0E8]/60 text-sm leading-relaxed">
                  From <em>The Soldier Vote</em> — a landmark in civil-military relations — to the upcoming novel <em>Wake of the Falcon</em>.
                </p>
              </div>
              <div className="flex items-center gap-2 text-[#C5A059] text-sm font-semibold mt-6 group-hover:gap-4 transition-all duration-300">
                View Publications <ArrowRight size={14} />
              </div>
            </Link>

            {/* Blog */}
            <Link
              to="/blog"
              data-testid="feature-card-blog"
              className="bento-card lg:col-span-7 bg-[#F5F0E8] p-8 group flex flex-col justify-between min-h-64"
            >
              <div>
                <div className="w-10 h-10 border border-[#0A1628] flex items-center justify-center mb-6">
                  <Rss size={20} className="text-[#0A1628]" />
                </div>
                <p className="overline mb-2">Latest Thoughts</p>
                <h3 className="font-serif text-2xl font-bold text-[#0A1628] mb-3">The Captain's Log</h3>
                {recentPosts.length > 0 ? (
                  <ul className="space-y-2">
                    {recentPosts.map(post => (
                      <li key={post.id} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-2 flex-shrink-0" />
                        <span className="text-sm text-[#2D3142] line-clamp-1">{post.title}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[#2D3142] text-sm">Naval History · Political Science · AD0K Radio · Music</p>
                )}
              </div>
              <div className="flex items-center gap-2 text-[#0A1628] text-sm font-semibold mt-6 group-hover:gap-4 transition-all duration-300">
                Read the Log <ArrowRight size={14} />
              </div>
            </Link>

            {/* Radio */}
            <Link
              to="/ad0k"
              data-testid="feature-card-radio"
              className="bento-card lg:col-span-7 bg-[#2D3142] p-8 group flex flex-col justify-between min-h-56"
            >
              <div>
                <div className="w-10 h-10 bg-[#C5A059] flex items-center justify-center mb-6">
                  <Radio size={20} className="text-[#0A1628]" />
                </div>
                <p className="overline text-[#C5A059] mb-2">Amateur Radio</p>
                <h3 className="font-serif text-2xl font-bold text-[#F5F0E8] mb-3">Station ADØK</h3>
                <p className="text-[#F5F0E8]/60 text-sm leading-relaxed">
                  Live station status, equipment details, and the story of how a naval officer became a licensed amateur radio operator.
                </p>
              </div>
              <div className="flex items-center gap-2 text-[#C5A059] text-sm font-semibold mt-6 group-hover:gap-4 transition-all duration-300">
                Visit Station <ArrowRight size={14} />
              </div>
            </Link>

            {/* Music */}
            <Link
              to="/music"
              data-testid="feature-card-music"
              className="bento-card lg:col-span-5 bg-[#F5F0E8] p-8 group flex flex-col justify-between min-h-56"
            >
              <div>
                <div className="w-10 h-10 border border-[#0A1628] flex items-center justify-center mb-6">
                  <Music size={20} className="text-[#0A1628]" />
                </div>
                <p className="overline mb-2">Live Music</p>
                <h3 className="font-serif text-2xl font-bold text-[#0A1628] mb-3">BuffaloGrass</h3>
                <p className="text-[#2D3142] text-sm leading-relaxed">
                  Americana roots music from the plains. Hear the band, find upcoming shows, and explore the sound.
                </p>
              </div>
              <div className="flex items-center gap-2 text-[#0A1628] text-sm font-semibold mt-6 group-hover:gap-4 transition-all duration-300">
                Explore Music <ArrowRight size={14} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Mailing List ── */}
      <section data-testid="mailing-list-section" className="py-24 bg-[#0A1628]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-12 h-12 bg-[#C5A059] flex items-center justify-center mx-auto mb-6">
            <Mail size={24} className="text-[#0A1628]" />
          </div>
          <p className="overline text-[#C5A059] mb-3">Stay Connected</p>
          <h2 className="font-serif text-3xl font-bold text-[#F5F0E8] mb-4">
            Join the Newsletter
          </h2>
          <p className="text-[#F5F0E8]/60 mb-8 leading-relaxed">
            New book announcements, blog posts, radio log highlights, and updates from the music front. No spam — ever.
          </p>
          {subMsg ? (
            <p data-testid="subscribe-success" className="text-[#C5A059] font-medium">{subMsg}</p>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                data-testid="mailing-list-input"
                className="flex-1 bg-[#F5F0E8]/10 border border-[#F5F0E8]/20 text-[#F5F0E8] placeholder-[#F5F0E8]/40 px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]"
              />
              <button
                type="submit"
                data-testid="mailing-list-submit"
                className="bg-[#C5A059] text-[#0A1628] px-6 py-3 text-sm font-semibold hover:bg-[#D4B577] transition-colors duration-200"
              >
                Subscribe
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
